// Settings > Dispatch > Send extra box — per-warehouse switch
// (settings.dispatch_extra_box, migration 232). When on, Admin > Orders can
// book one more courier parcel onto an order that has already shipped, for
// an item found after dispatch (Alex, 2026-10-22: Poland on).
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";

type SwitchState = { enabled: boolean; available: boolean };

const ExtraBoxSetting = ({ warehouses }: { warehouses: string[] }) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [state, setState] = useState<Record<string, SwitchState>>({});
    const [saving, setSaving] = useState<string | null>(null);

    // Keyed on the names, not the array: the parent builds a new array every render.
    const warehouseKey = warehouses.join("|");
    const load = useCallback(async () => {
        const next: Record<string, SwitchState> = {};
        await Promise.all(warehouses.map(async (wh) => {
            try {
                const res = await apiFetch(`/dispatch-admin/extra-box?warehouse=${encodeURIComponent(wh)}`, {});
                if (!res.ok) return;
                const data = await res.json();
                next[wh] = { enabled: data.enabled === true, available: data.available !== false };
            } catch {
                /* the switch just doesn't render for that warehouse */
            }
        }));
        setState(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [warehouseKey]);

    useEffect(() => { load(); }, [load]);

    const toggle = async (warehouse: string, enabled: boolean) => {
        if (readOnly) return;
        setSaving(warehouse);
        try {
            const res = await apiFetch(`/dispatch-admin/extra-box`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse, enabled }),
            });
            if (!res.ok) throw new Error("extra-box");
            const data = await res.json();
            setState((m) => ({ ...m, [warehouse]: { enabled: data.enabled === true, available: true } }));
        } catch {
            toast.error(t("server_error"), { toastId: "extra-box-setting-err" });
        } finally {
            setSaving(null);
        }
    };

    return (
        <div className="dispatch-admin-rows">
            {warehouses.filter((wh) => state[wh]).map((wh) => (
                <label key={wh} className="dispatch-admin-switch-row" data-testid={`extra-box-${wh}`}>
                    <span className="dispatch-admin-switch-text">
                        <span className="dispatch-admin-switch-title">{wh}</span>
                        {!state[wh].available && (
                            <span className="admin-settings-section-hint">{t("extra_box_setting_unavailable")}</span>
                        )}
                    </span>
                    <input
                        type="checkbox"
                        role="switch"
                        aria-label={`${t("extra_box_setting_title")} ${wh}`}
                        checked={state[wh].enabled}
                        disabled={readOnly || !state[wh].available || saving === wh}
                        onChange={(e) => toggle(wh, e.target.checked)}
                    />
                </label>
            ))}
        </div>
    );
};

export default ExtraBoxSetting;
